import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { marginApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, LoadingBlock, Select } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { PLANS, minimumPlanFor } from '@shared/constants/index.ts';
import type { MarginFloorSettings } from '@shared/types/index.ts';

/**
 * Settings → Margin floor (migration 0118): the lowest margin a discount may
 * leave on any quote line, what happens below it (refused, or an owner's or
 * manager's approval with a reason), and the loaded hourly rate a task with
 * no rate of its own is costed at. Each task's own cost basis is edited in
 * the task library.
 *
 * The owner edits; managers read. Pro (`profit_insights`).
 */
export default function MarginFloorSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can, company } = useSession();
  const canEdit = can('company:write');
  const currency = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const settings = useQuery({ queryKey: ['margin-settings'], queryFn: () => marginApi.settings() });
  const [draft, setDraft] = useState<MarginFloorSettings | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (settings.data) setDraft(settings.data);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (next: MarginFloorSettings) =>
      marginApi.updateSettings({
        floorBasisPoints: next.floorBasisPoints,
        mode: next.mode,
        defaultLoadedHourlyCents: next.defaultLoadedHourlyCents,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['margin-settings'], next);
      void queryClient.invalidateQueries({ queryKey: ['quote-margin'] });
      setMessage({ tone: 'success', text: t('marginFloor.saved') });
    },
    onError: (caught) =>
      setMessage({
        tone: 'error',
        text: caught instanceof ApiRequestError ? caught.message : t('marginFloor.saveError'),
      }),
  });

  const set = <K extends keyof MarginFloorSettings>(key: K, value: MarginFloorSettings[K]) => {
    setMessage(null);
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  return (
    <section className="card mb-6" aria-labelledby="margin-floor-heading" data-testid="margin-floor-section">
      <h2 id="margin-floor-heading" className="mb-1 text-lg font-semibold">
        {t('marginFloor.heading')}
      </h2>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('marginFloor.description')}</p>
      {settings.isLoading || !draft ? <LoadingBlock /> : null}
      {draft && !draft.available ? (
        <Alert tone="info">
          {t('marginFloor.planRequired', { plan: PLANS[minimumPlanFor('profit_insights')].name })}
        </Alert>
      ) : null}
      {draft && draft.available ? (
        <>
          <div className="grid gap-x-4 sm:grid-cols-3">
            <NumberInput
              label={t('marginFloor.floor')}
              hint={t('marginFloor.floorHint')}
              min={0}
              max={100}
              step={0.5}
              disabled={!canEdit}
              value={draft.floorBasisPoints / 100}
              onValueChange={(percent) => set('floorBasisPoints', Math.round((percent ?? 0) * 100))}
            />
            <Select
              label={t('marginFloor.mode')}
              value={draft.mode}
              disabled={!canEdit}
              options={[
                { value: 'override', label: t('marginFloor.modeOverride') },
                { value: 'block', label: t('marginFloor.modeBlock') },
                { value: 'off', label: t('marginFloor.modeOff') },
              ]}
              onChange={(event) => set('mode', event.target.value as MarginFloorSettings['mode'])}
            />
            <MoneyInput
              label={t('marginFloor.loadedRate')}
              hint={t('marginFloor.loadedRateHint')}
              valueCents={draft.defaultLoadedHourlyCents}
              currencyCode={currency}
              disabled={!canEdit}
              onChangeCents={(cents) => set('defaultLoadedHourlyCents', cents)}
            />
          </div>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">
            {t('marginFloor.taskCostsHint')}{' '}
            <Link to="/settings/templates" className="underline">
              {t('marginFloor.taskCostsLink')}
            </Link>
          </p>
          {message ? (
            <div className="mb-3">
              <Alert tone={message.tone}>{message.text}</Alert>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-3">
            {canEdit ? (
              <Button variant="primary" busy={save.isPending} onClick={() => save.mutate(draft)}>
                {t('marginFloor.save')}
              </Button>
            ) : null}
            {can('reports:read') ? (
              <Link to="/margin-overrides" className="btn">
                {t('marginFloor.viewOverrides')}
              </Link>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}
